import {
  EdgeType,
  LinkNegation,
  LinkQualifier,
  MMEdge,
} from '../model/edge-model';
import { Affiliation, Essence, MMNode, NodeType } from '../model/node-model';
import type { ChildGroupType } from '../model/node-model';

export enum OplEntityKind {
  StatelessObject = 'stateless-object',
  StatefulObject = 'stateful-object',
  Process = 'process',
  State = 'state',
  Invalid = 'invalid',
}

export interface OplNodeRecord {
  id: string;
  label: string;
  type: NodeType | string;
  parentId?: string;
  childGroupType?: ChildGroupType;
  essence?: Essence;
  affiliation?: Affiliation;
  deleted?: boolean;
}

export interface OplEdgeRecord {
  id: string;
  sourceId: string;
  targetId: string;
  type: EdgeType;
  label?: string;
  linkQualifier?: LinkQualifier;
  linkNegation?: LinkNegation;
}

export interface OplItem {
  id: string;
  text: string;
  edgeId?: string;
  nodeIds: string[];
  type: 'edge' | 'node';
  group?: 'definition' | 'relation';
}

export interface OplContext {
  nodeMap: Map<string, OplNodeRecord>;
  statesByParent: Map<string, OplNodeRecord[]>;
}

const isStateNode = (node: OplNodeRecord | null | undefined) => {
  if (!node) return false;
  return node.type === NodeType.State || node.type === 'state';
};

const isProcessNode = (node: OplNodeRecord | null | undefined) => {
  if (!node) return false;
  return node.type === NodeType.Process || node.type === 'process';
};

const isObjectNode = (node: OplNodeRecord | null | undefined) => {
  if (!node) return false;
  return node.type === NodeType.Object || node.type === 'object';
};

const hasStateChildren = (
  node: OplNodeRecord,
  statesByParent: Map<string, OplNodeRecord[]>
) => {
  if (!isObjectNode(node)) return false;
  const states = statesByParent.get(node.id);
  if (states && states.length > 0) return true;
  return false;
};

export const buildOplContext = (nodes: OplNodeRecord[]): OplContext => {
  const nodeMap = new Map<string, OplNodeRecord>();
  const statesByParent = new Map<string, OplNodeRecord[]>();
  nodes.forEach(node => {
    nodeMap.set(node.id, node);
    const parentId = node.parentId || '';
    if (isStateNode(node) && parentId && !node.deleted) {
      const bucket = statesByParent.get(parentId) || [];
      bucket.push(node);
      statesByParent.set(parentId, bucket);
    }
  });
  return { nodeMap, statesByParent };
};

export const classifyNodeRecord = (
  node: OplNodeRecord | null | undefined,
  ctx: OplContext
): OplEntityKind => {
  if (!node) return OplEntityKind.Invalid;
  if (isProcessNode(node)) return OplEntityKind.Process;
  if (isStateNode(node)) return OplEntityKind.State;
  if (isObjectNode(node)) {
    return hasStateChildren(node, ctx.statesByParent)
      ? OplEntityKind.StatefulObject
      : OplEntityKind.StatelessObject;
  }
  return OplEntityKind.Invalid;
};

export const classifyMMNode = (node: MMNode | null): OplEntityKind => {
  if (!node) return OplEntityKind.Invalid;
  if (node.type === NodeType.Process) return OplEntityKind.Process;
  if (node.type === NodeType.State) return OplEntityKind.State;
  if (node.type === NodeType.Object) {
    const hasStateChild = (node.children || []).some(
      child => child.type === NodeType.State && !child.deleted
    );
    return hasStateChild
      ? OplEntityKind.StatefulObject
      : OplEntityKind.StatelessObject;
  }
  return OplEntityKind.Invalid;
};

const isObjectKind = (kind: OplEntityKind) =>
  kind === OplEntityKind.StatefulObject ||
  kind === OplEntityKind.StatelessObject;

export const getAllowedEdgeTypesByKind = (
  sourceKind: OplEntityKind,
  targetKind: OplEntityKind
): EdgeType[] => {
  if (
    sourceKind === OplEntityKind.StatelessObject &&
    targetKind === OplEntityKind.StatelessObject
  ) {
    return [
      EdgeType.Aggregation,
      EdgeType.Exhibition,
      EdgeType.Generalization,
      EdgeType.Classification,
      EdgeType.Tagged,
      EdgeType.Bidirectional,
    ];
  }

  if (
    (sourceKind === OplEntityKind.StatelessObject &&
      targetKind === OplEntityKind.StatefulObject) ||
    (sourceKind === OplEntityKind.StatefulObject &&
      targetKind === OplEntityKind.StatelessObject) ||
    (sourceKind === OplEntityKind.StatefulObject &&
      targetKind === OplEntityKind.StatefulObject)
  ) {
    return [EdgeType.Exhibition];
  }

  if (isObjectKind(sourceKind) && targetKind === OplEntityKind.Process) {
    return [
      EdgeType.Exhibition,
      EdgeType.Agent,
      EdgeType.Instrument,
      EdgeType.Consumption,
      EdgeType.Effect,
    ];
  }

  if (
    sourceKind === OplEntityKind.State &&
    targetKind === OplEntityKind.Process
  ) {
    return [
      EdgeType.Exhibition,
      EdgeType.Agent,
      EdgeType.Instrument,
      EdgeType.Consumption,
    ];
  }

  if (sourceKind === OplEntityKind.Process && isObjectKind(targetKind)) {
    return [EdgeType.Exhibition, EdgeType.Generation, EdgeType.Effect];
  }

  if (
    sourceKind === OplEntityKind.Process &&
    targetKind === OplEntityKind.State
  ) {
    return [EdgeType.Generation];
  }

  if (
    sourceKind === OplEntityKind.Process &&
    targetKind === OplEntityKind.Process
  ) {
    return [EdgeType.Invocation];
  }

  return [];
};

export const getAllowedEdgeTypesByRecords = (
  sourceId: string,
  targetId: string,
  ctx: OplContext
): EdgeType[] => {
  const source = ctx.nodeMap.get(sourceId) || null;
  const target = ctx.nodeMap.get(targetId) || null;
  const sourceKind = classifyNodeRecord(source, ctx);
  const targetKind = classifyNodeRecord(target, ctx);
  return getAllowedEdgeTypesByKind(sourceKind, targetKind);
};

export const getAllowedEdgeTypesByMMNodes = (
  source: MMNode | null,
  target: MMNode | null
): EdgeType[] => {
  const sourceKind = classifyMMNode(source);
  const targetKind = classifyMMNode(target);
  return getAllowedEdgeTypesByKind(sourceKind, targetKind);
};

const createVirtualStatefulObject = (node: MMNode): MMNode => {
  return {
    ...node,
    children: [
      ...(node.children || []),
      {
        id: '__virtual_state__',
        type: NodeType.State,
        deleted: false,
      } as MMNode,
    ],
  } as MMNode;
};

export const getInvalidStateAdditionMessage = (
  node: MMNode | null,
  edges: MMEdge[]
): string | null => {
  if (!node || node.type !== NodeType.Object) return null;

  const virtualStatefulNode = createVirtualStatefulObject(node);

  for (const edge of edges) {
    if (!edge || edge.deleted) continue;
    if (edge.source !== node && edge.target !== node) continue;

    const source = edge.source === node ? virtualStatefulNode : edge.source;
    const target = edge.target === node ? virtualStatefulNode : edge.target;
    const allowedTypes = getAllowedEdgeTypesByMMNodes(source, target);

    if (!allowedTypes.includes(edge.type)) {
      return (
        getInvalidEdgeMessageByMMNodes(source, target) ||
        `当前对象已存在 ${edge.type} 连线，设置状态后将不再合法，请先调整相关连线`
      );
    }
  }

  return null;
};

export const getInvalidEdgeMessageByKinds = (
  _sourceKind: OplEntityKind,
  _targetKind: OplEntityKind
): string | null => {
  return null;
};

export const getInvalidEdgeMessageByMMNodes = (
  source: MMNode | null,
  target: MMNode | null
): string | null => {
  const sourceKind = classifyMMNode(source);
  const targetKind = classifyMMNode(target);
  return getInvalidEdgeMessageByKinds(sourceKind, targetKind);
};

const formatObjectName = (node: OplNodeRecord | null | undefined) =>
  node?.label || '';

const formatProcessName = (node: OplNodeRecord | null | undefined) =>
  node?.label || '';

const getStateOwnerName = (
  stateNode: OplNodeRecord,
  ctx: OplContext
): string => {
  const parent = ctx.nodeMap.get(stateNode.parentId || '') || null;
  return parent?.label || '';
};

const formatStateSubject = (
  stateNode: OplNodeRecord,
  ctx: OplContext
): string => {
  const parentLabel = getStateOwnerName(stateNode, ctx);
  return `${parentLabel} in ${stateNode.label}`;
};

const formatStateAtState = (
  stateNode: OplNodeRecord,
  ctx: OplContext
): string => {
  const parentLabel = getStateOwnerName(stateNode, ctx);
  return `${parentLabel} at state ${stateNode.label}`;
};

const formatStateLeading = (
  stateNode: OplNodeRecord,
  ctx: OplContext
): string => {
  const parentLabel = getStateOwnerName(stateNode, ctx);
  return `${stateNode.label} ${parentLabel}`;
};

const formatStateObject = (
  stateNode: OplNodeRecord,
  ctx: OplContext
): string => {
  const parentLabel = getStateOwnerName(stateNode, ctx);
  return `${parentLabel} in ${stateNode.label}`;
};

// Tag 文本用于单向/对等关联的 OPL 拼接
const normalizeEdgeTag = (label?: string) => (label ? label.trim() : '');

const buildTaggedRelation = (
  sourceText: string,
  targetText: string,
  label?: string
) => {
  const tag = normalizeEdgeTag(label);
  if (tag) return `${sourceText} ${tag} ${targetText}`;
  return `${sourceText} relates to ${targetText}`;
};

const buildEquivalentRelation = (
  leftText: string,
  rightText: string,
  label?: string
) => {
  const tag = normalizeEdgeTag(label);
  if (tag) return `${leftText} and ${rightText} are ${tag}`;
  return `${leftText} and ${rightText} are equivalent`;
};

const agentTemplate = (
  processName: string,
  objectPhrase: string,
  qualifier: LinkQualifier,
  negation: LinkNegation
): string => {
  if (qualifier === LinkQualifier.Event) {
    return `${objectPhrase} initiates and handles ${processName}`;
  }
  if (qualifier === LinkQualifier.Condition && negation === LinkNegation.Not) {
    return `${processName} occurs if ${objectPhrase} does not exist, otherwise ${processName} is skipped`;
  }
  if (qualifier === LinkQualifier.Condition) {
    return `${processName} occurs if ${objectPhrase} exists, otherwise ${processName} is skipped`;
  }
  if (negation === LinkNegation.Not) {
    return `${processName} occurs if ${objectPhrase} is not present`;
  }
  return `${objectPhrase} handles ${processName}`;
};

const stateAgentTemplate = (
  processName: string,
  stateNode: OplNodeRecord,
  ctx: OplContext,
  qualifier: LinkQualifier,
  negation: LinkNegation
): string => {
  if (qualifier === LinkQualifier.Event) {
    return `${formatStateLeading(stateNode, ctx)} initiates and handles ${processName}`;
  }
  if (qualifier === LinkQualifier.Condition && negation === LinkNegation.Not) {
    return `${processName} occurs if ${getStateOwnerName(
      stateNode,
      ctx
    )} is not at state ${stateNode.label}, otherwise ${processName} is skipped`;
  }
  if (qualifier === LinkQualifier.Condition) {
    return `${processName} occurs if ${getStateOwnerName(
      stateNode,
      ctx
    )} is ${stateNode.label}, otherwise ${processName} is skipped`;
  }
  if (negation === LinkNegation.Not) {
    return `${processName} occurs if ${formatStateAtState(
      stateNode,
      ctx
    )} is not present`;
  }
  return `${formatStateAtState(stateNode, ctx)} handles ${processName}`;
};

const instrumentTemplate = (
  processName: string,
  objectPhrase: string,
  qualifier: LinkQualifier,
  negation: LinkNegation
): string => {
  if (qualifier === LinkQualifier.Event) {
    return `${objectPhrase} initiates ${processName}, which requires ${objectPhrase}`;
  }
  if (negation === LinkNegation.Not && qualifier === LinkQualifier.Condition) {
    return `${processName} occurs if ${objectPhrase} does not exist, otherwise ${processName} is skipped`;
  }
  if (negation === LinkNegation.Not) {
    return `${processName} occurs if ${objectPhrase} does not exist`;
  }
  if (qualifier === LinkQualifier.Condition) {
    return `${processName} occurs if ${objectPhrase} exists, otherwise ${processName} is skipped`;
  }
  return `${processName} requires ${objectPhrase}`;
};

const stateInstrumentTemplate = (
  processName: string,
  stateNode: OplNodeRecord,
  ctx: OplContext,
  qualifier: LinkQualifier,
  negation: LinkNegation
): string => {
  if (qualifier === LinkQualifier.Event) {
    return `${formatStateLeading(
      stateNode,
      ctx
    )} initiates ${processName}, which requires ${formatStateLeading(
      stateNode,
      ctx
    )}`;
  }
  if (qualifier === LinkQualifier.Condition && negation === LinkNegation.Not) {
    return `${processName} occurs if ${getStateOwnerName(
      stateNode,
      ctx
    )} is not at state ${stateNode.label}, otherwise ${processName} is skipped`;
  }
  if (qualifier === LinkQualifier.Condition) {
    return `${processName} occurs if ${getStateOwnerName(
      stateNode,
      ctx
    )} is at state ${stateNode.label}, otherwise ${processName} is skipped`;
  }
  if (negation === LinkNegation.Not) {
    return `${processName} occurs if ${formatStateAtState(
      stateNode,
      ctx
    )} does not exist`;
  }
  return `${processName} requires ${formatStateAtState(stateNode, ctx)}`;
};

const consumptionTemplate = (
  processName: string,
  objectPhrase: string,
  qualifier: LinkQualifier
): string => {
  if (qualifier === LinkQualifier.Event) {
    return `${objectPhrase} initiates ${processName}, which consumes ${objectPhrase}`;
  }
  if (qualifier === LinkQualifier.Condition) {
    return `${processName} occurs if ${objectPhrase} exists, in which case ${processName} consumes ${objectPhrase}, otherwise ${processName} is skipped`;
  }
  return `${processName} consumes ${objectPhrase}`;
};

const stateConsumptionTemplate = (
  processName: string,
  stateNode: OplNodeRecord,
  ctx: OplContext,
  qualifier: LinkQualifier
): string => {
  if (qualifier === LinkQualifier.Event) {
    return `${formatStateLeading(
      stateNode,
      ctx
    )} initiates ${processName}, which consumes ${getStateOwnerName(
      stateNode,
      ctx
    )}`;
  }
  if (qualifier === LinkQualifier.Condition) {
    return `${processName} occurs if ${getStateOwnerName(
      stateNode,
      ctx
    )} is at state ${stateNode.label}, in which case ${processName} consumes ${formatStateLeading(
      stateNode,
      ctx
    )}, otherwise ${processName} is skipped`;
  }
  return `${processName} consumes ${formatStateAtState(stateNode, ctx)}`;
};

const effectTemplate = (
  processName: string,
  objectPhrase: string,
  qualifier: LinkQualifier
): string => {
  if (qualifier === LinkQualifier.Event) {
    return `${objectPhrase} initiates ${processName}, which affects ${objectPhrase}`;
  }
  if (qualifier === LinkQualifier.Condition) {
    return `${processName} occurs if ${objectPhrase} exists, in which case ${processName} affects ${objectPhrase}, otherwise ${processName} is skipped`;
  }
  return `${processName} affects ${objectPhrase}`;
};

const fallbackRelation = (
  edge: OplEdgeRecord,
  sourceLabel: string,
  targetLabel: string
) => `${sourceLabel} relates to ${targetLabel} (${edge.type})`;

export const generateOplSentence = (
  edge: OplEdgeRecord,
  ctx: OplContext
): string | null => {
  const source = ctx.nodeMap.get(edge.sourceId) || null;
  const target = ctx.nodeMap.get(edge.targetId) || null;
  if (!source || !target) return null;

  const sourceKind = classifyNodeRecord(source, ctx);
  const targetKind = classifyNodeRecord(target, ctx);
  const qualifier = edge.linkQualifier ?? LinkQualifier.None;
  const negation = edge.linkNegation ?? LinkNegation.None;

  const sourceObjectName = formatObjectName(source);
  const targetObjectName = formatObjectName(target);
  const sourceProcessName = formatProcessName(source);
  const targetProcessName = formatProcessName(target);

  switch (edge.type) {
    case EdgeType.Aggregation:
      if (
        sourceKind === OplEntityKind.StatelessObject &&
        targetKind === OplEntityKind.StatelessObject
      ) {
        return `${sourceObjectName} consists of ${targetObjectName}`;
      }
      return fallbackRelation(edge, sourceObjectName, targetObjectName);

    case EdgeType.Exhibition:
      if (isObjectKind(sourceKind) && targetKind === OplEntityKind.Process) {
        return `${sourceObjectName} exhibits ${targetProcessName}`;
      }
      if (
        sourceKind === OplEntityKind.State &&
        targetKind === OplEntityKind.Process
      ) {
        return `${formatStateSubject(source, ctx)} exhibits ${targetProcessName}`;
      }
      if (sourceKind === OplEntityKind.Process && isObjectKind(targetKind)) {
        return `${sourceProcessName} exhibits ${targetObjectName}`;
      }
      if (isObjectKind(sourceKind) && isObjectKind(targetKind)) {
        return `${sourceObjectName} exhibits ${targetObjectName}`;
      }
      return fallbackRelation(edge, sourceObjectName, targetObjectName);

    case EdgeType.Generalization:
      if (
        sourceKind === OplEntityKind.StatelessObject &&
        targetKind === OplEntityKind.StatelessObject
      ) {
        return `${targetObjectName} is a ${sourceObjectName}`;
      }
      return fallbackRelation(edge, sourceObjectName, targetObjectName);

    case EdgeType.Classification:
      if (
        sourceKind === OplEntityKind.StatelessObject &&
        targetKind === OplEntityKind.StatelessObject
      ) {
        return `${targetObjectName} is an instance of ${sourceObjectName}`;
      }
      return fallbackRelation(edge, sourceObjectName, targetObjectName);

    case EdgeType.Tagged:
      if (
        sourceKind === OplEntityKind.StatelessObject &&
        targetKind === OplEntityKind.StatelessObject
      ) {
        return buildTaggedRelation(
          sourceObjectName,
          targetObjectName,
          edge.label
        );
      }
      return fallbackRelation(edge, sourceObjectName, targetObjectName);

    case EdgeType.Bidirectional:
      if (
        sourceKind === OplEntityKind.StatelessObject &&
        targetKind === OplEntityKind.StatelessObject
      ) {
        return buildEquivalentRelation(
          sourceObjectName,
          targetObjectName,
          edge.label
        );
      }
      return fallbackRelation(edge, sourceObjectName, targetObjectName);

    case EdgeType.Agent: {
      if (isObjectKind(sourceKind) && targetKind === OplEntityKind.Process) {
        return agentTemplate(
          targetProcessName,
          sourceObjectName,
          qualifier,
          negation
        );
      }
      if (
        sourceKind === OplEntityKind.State &&
        targetKind === OplEntityKind.Process
      ) {
        return stateAgentTemplate(
          targetProcessName,
          source,
          ctx,
          qualifier,
          negation
        );
      }
      return fallbackRelation(edge, sourceObjectName, targetObjectName);
    }

    case EdgeType.Instrument: {
      if (isObjectKind(sourceKind) && targetKind === OplEntityKind.Process) {
        return instrumentTemplate(
          targetProcessName,
          sourceObjectName,
          qualifier,
          negation
        );
      }
      if (
        sourceKind === OplEntityKind.State &&
        targetKind === OplEntityKind.Process
      ) {
        return stateInstrumentTemplate(
          targetProcessName,
          source,
          ctx,
          qualifier,
          negation
        );
      }
      return fallbackRelation(edge, sourceObjectName, targetObjectName);
    }

    case EdgeType.Consumption: {
      if (isObjectKind(sourceKind) && targetKind === OplEntityKind.Process) {
        return consumptionTemplate(
          targetProcessName,
          sourceObjectName,
          qualifier
        );
      }
      if (
        sourceKind === OplEntityKind.State &&
        targetKind === OplEntityKind.Process
      ) {
        return stateConsumptionTemplate(
          targetProcessName,
          source,
          ctx,
          qualifier
        );
      }
      return fallbackRelation(edge, sourceObjectName, targetObjectName);
    }

    case EdgeType.Effect: {
      if (isObjectKind(sourceKind) && targetKind === OplEntityKind.Process) {
        return effectTemplate(targetProcessName, sourceObjectName, qualifier);
      }
      if (sourceKind === OplEntityKind.Process && isObjectKind(targetKind)) {
        return effectTemplate(sourceProcessName, targetObjectName, qualifier);
      }
      return fallbackRelation(edge, sourceObjectName, targetObjectName);
    }

    case EdgeType.Generation: {
      if (sourceKind === OplEntityKind.Process && isObjectKind(targetKind)) {
        return `${sourceProcessName} yields ${targetObjectName}`;
      }
      if (
        sourceKind === OplEntityKind.Process &&
        targetKind === OplEntityKind.State
      ) {
        return `${sourceProcessName} yields ${formatStateObject(target, ctx)}`;
      }
      return fallbackRelation(edge, sourceObjectName, targetObjectName);
    }

    case EdgeType.Invocation:
      if (
        sourceKind === OplEntityKind.Process &&
        targetKind === OplEntityKind.Process
      ) {
        return `${sourceProcessName} invoke ${targetProcessName}`;
      }
      return fallbackRelation(edge, sourceObjectName, targetObjectName);

    default:
      return fallbackRelation(edge, sourceObjectName, targetObjectName);
  }
};

export const buildNodeDefinitionItems = (ctx: OplContext): OplItem[] => {
  const items: OplItem[] = [];
  ctx.nodeMap.forEach(node => {
    const kind = classifyNodeRecord(node, ctx);
    if (kind === OplEntityKind.Process) {
      const essenceText =
        node.essence === Essence.Physical ? 'physical' : 'informatical';
      const affiliationText =
        node.affiliation === Affiliation.Environmental
          ? 'environmental'
          : 'systemic';
      items.push({
        id: `def-${node.id}`,
        text: `${node.label} is an ${essenceText} and ${affiliationText} process.`,
        nodeIds: [node.id],
        type: 'node',
        group: 'definition',
      });
    }
    if (isObjectNode(node)) {
      const essenceText =
        node.essence === Essence.Physical ? 'physical' : 'informatical';
      const affiliationText =
        node.affiliation === Affiliation.Environmental
          ? 'environmental'
          : 'systemic';
      items.push({
        id: `def-${node.id}`,
        text: `${node.label} is an ${essenceText} and ${affiliationText} object.`,
        nodeIds: [node.id],
        type: 'node',
        group: 'definition',
      });
      const states = ctx.statesByParent.get(node.id);
      if (states && states.length) {
        const stateNames = states.map(item => item.label).join('、');
        items.push({
          id: `def-states-${node.id}`,
          text: `${node.label} can be ${stateNames}`,
          nodeIds: [node.id, ...states.map(s => s.id)],
          type: 'node',
          group: 'definition',
        });
      }
    }
  });
  return items;
};

export const buildRelationItems = (
  edges: OplEdgeRecord[],
  ctx: OplContext
): OplItem[] => {
  const items: OplItem[] = [];
  edges.forEach(edge => {
    const text = generateOplSentence(edge, ctx);
    if (!text) return;
    items.push({
      id: `edge-${edge.id}`,
      text,
      edgeId: edge.id,
      nodeIds: [edge.sourceId, edge.targetId],
      type: 'edge',
      group: 'relation',
    });
  });
  return items;
};
